import { MapPin, Navigation, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LocationInfo, VenueInfo } from '../types';

interface LocationContentProps {
  location?: LocationInfo;
  venue?: VenueInfo;
  isOutgoing?: boolean;
  className?: string;
}

/**
 * Компонент для отображения геолокации или места (venue)
 */
export function LocationContent({ location, venue, isOutgoing, className }: LocationContentProps) {
  // Use venue coordinates if available, otherwise location
  const lat = venue?.lat ?? location?.lat;
  const long = venue?.long ?? location?.long;

  if (lat === undefined || long === undefined) {
    return (
      <div className={cn('flex items-center gap-2 py-2', className)}>
        <MapPin className="h-5 w-5 opacity-75" />
        <span className="text-sm italic opacity-75">Геопозиция недоступна</span>
      </div>
    );
  }

  // Static map image from OpenStreetMap tile server
  // Using zoom level 15 for good detail
  const zoom = 15;
  const mapWidth = 240;
  const mapHeight = 150;

  // Calculate tile coordinates
  const n = Math.pow(2, zoom);
  const xtile = Math.floor((long + 180) / 360 * n);
  const ytile = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * n);

  // OpenStreetMap tile URL
  const tileUrl = `https://tile.openstreetmap.org/${zoom}/${xtile}/${ytile}.png`;

  // Google Maps link for opening
  const mapsUrl = `https://www.google.com/maps?q=${lat},${long}`;

  const isLiveLocation = location?.livePeriod !== undefined;

  return (
    <div className={cn('overflow-hidden', className)}>
      {/* Map preview */}
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="block relative group"
      >
        <div
          className="relative bg-muted overflow-hidden"
          style={{ width: mapWidth, height: mapHeight }}
        >
          {/* Map tile */}
          <img
            src={tileUrl}
            alt="Карта"
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to placeholder on error
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />

          {/* Pin marker overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative">
              <MapPin
                className={cn(
                  'h-8 w-8 drop-shadow-lg',
                  isLiveLocation ? 'text-green-500' : 'text-red-500'
                )}
                fill="currentColor"
              />
              {isLiveLocation && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
                </span>
              )}
            </div>
          </div>

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <ExternalLink className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
          </div>
        </div>
      </a>

      {/* Venue info */}
      {venue && (
        <div className="px-2 py-2">
          <p className="font-medium text-sm">{venue.title}</p>
          <p
            className={cn(
              'text-xs',
              isOutgoing ? 'text-white/70' : 'text-muted-foreground'
            )}
          >
            {venue.address}
          </p>
        </div>
      )}

      {/* Live location indicator */}
      {isLiveLocation && !venue && (
        <div className="px-2 py-1 flex items-center gap-1.5">
          <Navigation
            className={cn(
              'h-3.5 w-3.5',
              isOutgoing ? 'text-white/80' : 'text-green-500'
            )}
          />
          <span
            className={cn(
              'text-xs',
              isOutgoing ? 'text-white/80' : 'text-muted-foreground'
            )}
          >
            Геопозиция в реальном времени
          </span>
        </div>
      )}

      {/* Coordinates */}
      {!venue && !isLiveLocation && (
        <div className="px-2 py-1">
          <span
            className={cn(
              'text-xs',
              isOutgoing ? 'text-white/70' : 'text-muted-foreground'
            )}
          >
            {lat.toFixed(6)}, {long.toFixed(6)}
          </span>
        </div>
      )}
    </div>
  );
}
